import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  createGradeSchema,
  deleteGradeSchema,
  fetchGradeSchemas,
  fetchGradeSchemaSubjectDefaults,
  setSubjectDefaultGradeSchema,
  updateGradeSchema,
  type GradeSchemaBand,
  type GradeSchemaRow,
  previewGradeSchemaMatch
} from '../../lib/classworkApi';
import { fetchTeachingMeta } from '../../lib/unterrichtenApi';

const DE_1_6_TEMPLATE: { bands: GradeSchemaBand[] } = {
  bands: [
    { minPercent: 92, grade: '1', numeric: 1 },
    { minPercent: 81, grade: '2', numeric: 2 },
    { minPercent: 67, grade: '3', numeric: 3 },
    { minPercent: 50, grade: '4', numeric: 4 },
    { minPercent: 30, grade: '5', numeric: 5 },
    { minPercent: 0, grade: '6', numeric: 6 }
  ]
};

function parseScale(row: GradeSchemaRow): { bands: GradeSchemaBand[] } {
  const raw = row.scale_json;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return { bands: [] };
    }
  }
  if (raw && typeof raw === 'object' && Array.isArray((raw as { bands?: unknown }).bands)) {
    return raw as { bands: GradeSchemaBand[] };
  }
  return { bands: [] };
}

export default function GradeSchemasPanel({ isAdmin }: { isAdmin: boolean }) {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<number | 'new' | null>(null);
  const [formName, setFormName] = useState('');
  const [formBands, setFormBands] = useState<GradeSchemaBand[]>(DE_1_6_TEMPLATE.bands);
  const [previewPct, setPreviewPct] = useState('75');
  const [previewNote, setPreviewNote] = useState<string | null>(null);

  const schemasQuery = useQuery(['grade-schemas-admin', isAdmin], () => fetchGradeSchemas({ includeInactive: isAdmin }));
  const defaultsQuery = useQuery(['grade-schema-subject-defaults'], fetchGradeSchemaSubjectDefaults);
  const metaQuery = useQuery(['teaching-meta-noten'], fetchTeachingMeta);

  const subjects = useMemo(() => {
    const list = metaQuery.data?.subjects || [];
    return [...list].sort((a, b) => String(a.name).localeCompare(String(b.name), 'de'));
  }, [metaQuery.data?.subjects]);

  const defaultsMap = defaultsQuery.data || {};

  const saveMutation = useMutation(
    async () => {
      if (!formName.trim()) throw new Error('name');
      const payload = { name: formName.trim(), scale_type: 'numeric' as const, scale_json: { bands: formBands } };
      if (editingId === 'new') {
        return createGradeSchema(payload);
      }
      if (typeof editingId === 'number') {
        return updateGradeSchema(editingId, payload);
      }
      throw new Error('state');
    },
    {
      onSuccess: () => {
        toast.success('Notenschema gespeichert');
        queryClient.invalidateQueries(['grade-schemas-admin']);
        queryClient.invalidateQueries(['classwork-grade-schemas']);
        setEditingId(null);
      },
      onError: (e: Error) => {
        toast.error(e?.message === 'name' ? 'Bitte einen Namen angeben' : 'Speichern fehlgeschlagen');
      }
    }
  );

  const deleteMutation = useMutation((id: number) => deleteGradeSchema(id), {
    onSuccess: () => {
      toast.success('Schema deaktiviert');
      queryClient.invalidateQueries(['grade-schemas-admin']);
      queryClient.invalidateQueries(['classwork-grade-schemas']);
    },
    onError: () => {
      toast.error('Löschen fehlgeschlagen');
    }
  });

  const setDefaultMut = useMutation(
    ({ subjectId, schemaId }: { subjectId: number; schemaId: number | null }) =>
      setSubjectDefaultGradeSchema(subjectId, schemaId),
    {
      onSuccess: () => {
        toast.success('Standard gespeichert');
        queryClient.invalidateQueries(['grade-schema-subject-defaults']);
        queryClient.invalidateQueries(['grade-default-subject']);
      },
      onError: () => {
        toast.error('Standard konnte nicht gespeichert werden');
      }
    }
  );

  const startEdit = (row: GradeSchemaRow | null) => {
    if (!row) {
      setEditingId('new');
      setFormName('');
      setFormBands([...DE_1_6_TEMPLATE.bands]);
      return;
    }
    setEditingId(row.id);
    setFormName(row.name);
    setFormBands(parseScale(row).bands.length ? parseScale(row).bands : [...DE_1_6_TEMPLATE.bands]);
  };

  const runPreview = async () => {
    if (typeof editingId !== 'number') {
      toast.error('Zuerst Schema speichern, dann Vorschau mit gespeicherter ID');
      return;
    }
    const pct = Number(previewPct);
    if (Number.isNaN(pct) || pct < 0 || pct > 100) {
      toast.error('Prozent 0–100');
      return;
    }
    try {
      const data = await previewGradeSchemaMatch(editingId, pct);
      setPreviewNote(data?.grade_value != null ? String(data.grade_value) : '—');
    } catch {
      toast.error('Vorschau fehlgeschlagen');
    }
  };

  const rows = schemasQuery.data || [];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-50 mb-1">Notenschemata</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Prozentbereiche (absteigend nach <code className="text-xs rounded bg-gray-100 dark:bg-gray-800 px-1 dark:bg-gray-800 dark:text-gray-200">minPercent</code>) werden der Note
          zugeordnet – wie im Notenbuch bei der Klassenarbeit-Finalisierung.
        </p>
        <button type="button" className="btn-primary text-sm" onClick={() => startEdit(null)}>
          Neues Schema
        </button>
      </div>

      {(editingId === 'new' || typeof editingId === 'number') && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 space-y-4">
          <h3 className="font-medium text-gray-900 dark:text-gray-50">{editingId === 'new' ? 'Neues Notenschema' : `Schema bearbeiten (#${editingId})`}</h3>
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Name</label>
            <input className="input-field max-w-md" value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="z. B. Sek I 1–6" />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Prozent → Note</span>
              <button
                type="button"
                className="text-xs text-primary-700 underline"
                onClick={() => setFormBands([...DE_1_6_TEMPLATE.bands])}
              >
                Vorlage 1–6 (DE) laden
              </button>
              <button
                type="button"
                className="text-xs text-gray-600 dark:text-gray-400 underline"
                onClick={() => setFormBands((b) => [...b, { minPercent: 0, grade: '', numeric: undefined }])}
              >
                Zeile +
              </button>
            </div>
            <div className="overflow-x-auto border border-gray-100 dark:border-gray-800 rounded-md">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800/50 text-left text-xs text-gray-600 dark:text-gray-400">
                    <th className="px-3 py-2">Min. % (≥)</th>
                    <th className="px-3 py-2">Note (Text)</th>
                    <th className="px-3 py-2">Numerisch (optional)</th>
                    <th className="px-3 py-2 w-16" />
                  </tr>
                </thead>
                <tbody>
                  {formBands.map((b, i) => (
                    <tr key={i} className="border-t border-gray-100 dark:border-gray-800">
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          className="input-field !py-1 !text-sm w-24"
                          min={0}
                          max={100}
                          step={1}
                          value={b.minPercent}
                          onChange={(e) => {
                            const v = Number(e.target.value);
                            const next = [...formBands];
                            next[i] = { ...next[i], minPercent: Number.isFinite(v) ? v : 0 };
                            setFormBands(next);
                          }}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          className="input-field !py-1 !text-sm w-28"
                          value={b.grade}
                          onChange={(e) => {
                            const next = [...formBands];
                            next[i] = { ...next[i], grade: e.target.value };
                            setFormBands(next);
                          }}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          className="input-field !py-1 !text-sm w-24"
                          step={0.1}
                          value={b.numeric ?? ''}
                          onChange={(e) => {
                            const next = [...formBands];
                            const raw = e.target.value;
                            next[i] = {
                              ...next[i],
                              numeric: raw === '' ? undefined : Number(raw)
                            };
                            setFormBands(next);
                          }}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          className="text-red-600 dark:text-red-400 text-xs"
                          onClick={() => setFormBands(formBands.filter((_, j) => j !== i))}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Die höchste passende Schwelle gewinnt (z. B. 75 % ≥ 67 % → Note 3).
            </p>
          </div>
          {typeof editingId === 'number' && (
            <div className="flex flex-wrap items-end gap-2 border-t border-gray-100 dark:border-gray-800 pt-3">
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Test-Prozent</label>
                <input className="input-field !py-1 w-24" value={previewPct} onChange={(e) => setPreviewPct(e.target.value)} />
              </div>
              <button type="button" className="btn-secondary text-sm" onClick={() => void runPreview()}>
                Vorschau-Note
              </button>
              {previewNote != null && (
                <span className="text-sm text-gray-800 dark:text-gray-200">
                  Ergebnis: <strong>{previewNote}</strong>
                </span>
              )}
            </div>
          )}
          <div className="flex gap-2">
            <button type="button" className="btn-primary text-sm" disabled={saveMutation.isLoading} onClick={() => saveMutation.mutate()}>
              Speichern
            </button>
            <button type="button" className="btn-secondary text-sm" onClick={() => setEditingId(null)}>
              Abbrechen
            </button>
          </div>
        </div>
      )}

      <div>
        <h3 className="text-md font-medium text-gray-900 dark:text-gray-50 mb-2">Vorhandene Schemata</h3>
        {schemasQuery.isLoading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Laden …</p>
        ) : (
          <ul className="divide-y divide-gray-100 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900">
            {rows.map((r) => (
              <li key={r.id} className="px-3 py-2 flex flex-wrap items-center justify-between gap-2 text-sm">
                <div>
                  <span className="font-medium text-gray-900 dark:text-gray-50">{r.name}</span>
                  <span className="text-gray-500 dark:text-gray-400 ml-2">#{r.id}</span>
                  {Number(r.is_active) === 0 && <span className="ml-2 text-amber-700 text-xs">(inaktiv)</span>}
                </div>
                <div className="flex gap-2">
                  <button type="button" className="text-primary-700 text-xs hover:underline" onClick={() => startEdit(r)}>
                    Bearbeiten
                  </button>
                  {Number(r.is_active) !== 0 && (
                    <button
                      type="button"
                      className="text-red-600 dark:text-red-400 text-xs hover:underline"
                      onClick={() => {
                        if (confirm('Schema deaktivieren? (bestehende Verweise bleiben erhalten)')) deleteMutation.mutate(r.id);
                      }}
                    >
                      Deaktivieren
                    </button>
                  )}
                </div>
              </li>
            ))}
            {!rows.length && <li className="px-3 py-4 text-gray-500 dark:text-gray-400 text-sm">Noch keine Schemata – „Neues Schema“ anlegen.</li>}
          </ul>
        )}
      </div>

      <div>
        <h3 className="text-md font-medium text-gray-900 dark:text-gray-50 mb-1">Standard-Notenschema pro Fach</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
          Wird in der Klassenarbeit bei der Bewertung vorausgewählt, wenn die Klassenarbeit diesem Fach zugeordnet ist.
        </p>
        {metaQuery.isLoading || defaultsQuery.isLoading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Fächer laden …</p>
        ) : (
          <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800/50 text-left text-xs text-gray-600 dark:text-gray-400">
                  <th className="px-3 py-2">Fach</th>
                  <th className="px-3 py-2">Standard-Schema</th>
                </tr>
              </thead>
              <tbody>
                {subjects.map((s) => {
                  const gid = defaultsMap[s.id]?.grade_schema_id;
                  const selectValue = gid != null ? String(gid) : '';
                  return (
                    <tr key={s.id} className="border-t border-gray-100 dark:border-gray-800">
                      <td className="px-3 py-2">{s.name}</td>
                      <td className="px-3 py-2">
                        <select
                          className="input-field !py-1 !text-sm min-w-[220px]"
                          value={selectValue}
                          onChange={(e) => {
                            const v = e.target.value;
                            setDefaultMut.mutate({
                              subjectId: s.id,
                              schemaId: v === '' ? null : Number(v)
                            });
                          }}
                        >
                          <option value="">— keines —</option>
                          {rows
                            .filter((r) => Number(r.is_active) !== 0)
                            .map((r) => (
                              <option key={r.id} value={String(r.id)}>
                                {r.name}
                              </option>
                            ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
